import {Component} from '@angular/core';
import {
  ArticleBodyDirective,
  ArticleTitleDirective,
  PolicyArticleComponent
} from "projects/soko-console-web-v2/src/app/features/support/policies/policy-article/policy-article.component";

@Component({
  selector: 'app-fair-usage-policy-article',
  standalone: true,
  imports: [
    PolicyArticleComponent, ArticleTitleDirective, ArticleBodyDirective
  ],
  styleUrl: './styles/support-policy-articles-common.scss',
  template: `
    <app-policy-article>
      <span articleTitle>Fair Usage Policy</span>
      <div articleBody class="body-1-regular-1">

        <section>
          <div class="heading">Introduction</div>
          <div>This Fair Usage Policy (FUP) applies to all subscribers and users of the platform. This policy is
            designed to ensure the platform's resources are used effectively and responsibly, ensuring a
            high-quality experience for all users.
          </div>
        </section>
        <section>
          <div class="heading">Scope of Use</div>
          <div>The platform is intended for retail leads and leasing management, related financial transaction
            processing, space planning, and related activities within the retail industry. Users are required to use
            the platform in a manner consistent with its intended purpose and only for lawful business operations.
          </div>
        </section>
        <section>
          <div class="heading">Resource Usage and Limitations</div>
          <div>Our platform is designed to provide a robust and scalable service to meet the needs of retail leasing
            and fin-tech sectors. To maintain the highest service quality for all our users, we apply fair usage
            policies to various resources, including but not limited to, emails/notifications, document storage, API
            calls, and system processing power.
          </div>

          <ul>

            <li>
              Emails/Notifications: Users are allocated a certain number of emails and notifications per month as
              specified in their License Agreement. Exceeding this allocation may result in additional charges or
              the need to upgrade your service package.
            </li>
            <li>
              Document Storage: A specific amount of digital storage space is allocated for each user for storing
              documents and files, as detailed in the License Agreement. Overage of this allocation will incur
              additional charges as per the agreed terms.
            </li>
            <li>
              API Calls: To ensure equitable access and the smooth functioning of our services, a fair usage limit
              is also placed on the number of API calls a user can make within a given timeframe. This limit is
              designed to prevent excessive use that could impact system performance for other users. Should these
              limits be exceeded, we may reach out to discuss potential solutions, including service upgrades or
              additional charges.
            </li>
            <li>
              Computational Resource Consumption: Utilization of computational resources related to the platform’s
              use is monitored to ensure equitable distribution and prevent the disproportionate consumption by any
              single user's activities. Actions initiated by users, which directly or indirectly engage significant
              computational resources, are subject to monitoring. Should such activities persistently demand high
              levels of computational power, we may reach out to discuss strategies for more efficient use or
              possible adjustments to service agreements.
            </li>
            <li>
              General Resource Usage: In addition to the specific resources mentioned above, we reserve the right to
              monitor and regulate the usage of all platform resources. This includes data transfer bandwidth,
              number of concurrent sessions, and any other resource that may affect the service quality for other
              users. Users consistently exceeding normal usage patterns, may be subject to review and further
              action, which could include recommendations for service package adjustments, imposition of additional
              charges, or modification of resource allocation.
            </li>
          </ul>
        </section>
        <section>
          <div class="heading"> Adaptive Usage Limits</div>

          <div> To support our commitment to providing a high-quality service to all users, we regularly review and
            adjust our resource usage limits in line with technological advancements and user demand. This adaptive
            approach ensures our platform remains performant, secure, and capable of meeting our users' evolving
            needs.
          </div>
        </section>
        <section>
          <div class="heading">Efforts to Notify on Resource Usage Overages</div>
          <div>The platform will make all reasonable attempts to inform users who are approaching or have surpassed
            their
            allocated resource usage limits through the platform's standard communication channels. These efforts are
            intended to offer users the chance to modify their usage patterns or explore options for service package
            enhancements in advance of any potential restrictions or the application of overage charges. However, it
            is the responsibility of users to monitor and manage their resource usage, as notifications are a courtesy
            and cannot be guaranteed in every instance.
          </div>
        </section>
        <section>
          <div class="heading">Fair and Acceptable Use</div>
          This policy outlines the expected conduct for all users of the platform to ensure a secure, legal, and
          efficient environment for everyone.
        </section>
        <section>
          <div class="heading">Data Privacy</div>
          <div>Users are obligated to respect and comply with applicable privacy laws and regulations. Any personal
            data
            accessed or collected through the platform must not be utilized for unauthorized purposes. This
            includes,
            but is not limited to, the sharing, selling, or misuse of such data.
          </div>
        </section>
        <section>
          <div class="heading">Intellectual Property</div>
          <div>The platform is a repository of content and data that are protected by copyright laws. As a user, you are
            responsible for ensuring that your use of the platform does not infringe on the intellectual property
            rights of others, including copyrights, trademarks, and patents. Unauthorized reproduction, distribution,
            or modification of any content on the platform is strictly prohibited.
          </div>
        </section>
        <section>
          <div class="heading">Compliance with Laws</div>
          <div>Users must ensure their use of the platform adheres to all applicable local, state, national, and
            international laws and regulations. This encompasses compliance in areas such as finance, data protection,
            and export control laws, among others.
          </div>
        </section>
        <section>
          <div class="heading">Prohibited Activities</div>
          <div>To maintain the integrity and functionality of the platform, the following activities are expressly
            forbidden:
          </div>
          <ul>
            <li>Engaging in any illegal activities or using the platform in violation of any laws or regulations.
            </li>
            <li>Performing actions that are fraudulent, misleading, or deceptive.</li>
            <li>Circumventing or attempting to circumvent billing mechanisms or resource allocation systems.</li>
            <li>Sharing or disclosing sensitive information without proper authorization, which includes financial
              details and personal identification information.
            </li>
            <li>Circumventing System Processes: Users are prohibited from bypassing, modifying, or interfering with
              any system processes, security measures, or resource allocation protocols. This includes attempts to
              gain unauthorized access to parts of the platform or its data, or to alter or tamper with platform
              data or operations.
            </li>
            <li>Data Integrity and Accuracy: Users are responsible for ensuring the accuracy and integrity of data
              they enter or upload to the platform. The platform operates on the assumption that all user-provided
              data is correct and complete. Users must promptly correct any data inaccuracies or inconsistencies
              they become aware of.
            </li>
          </ul>
        </section>
        <section>
          <div class="heading">Responsibility and Enforcement</div>
          <div>Violations of this Fair and Acceptable Use Policy may result in disciplinary action, up to and including
            account suspension or termination, legal action, and financial liability for any damages caused by the
            violation.

            The platform reserves the right to investigate suspected violations and enforce this policy as deemed
            appropriate. Users are encouraged to report any suspected abuse or violations to platform administration.
          </div>
        </section>
        <section>
          <div class="heading">Enforcement and Remediation</div>
          <div>Violations of this policy may result in immediate suspension or termination of the user's account, legal
            action, and charges for recovery costs associated with any misuse. Nter reserves the right to investigate
            suspected violations of this FUP.
          </div>
        </section>
        <section>
          <div class="heading">Reporting Misuse</div>
          <div>Users are encouraged to report any suspected misuse of the platform or violations of this policy.</div>
        </section>
        <section>
          <div class="heading">Amendments to the Policy</div>
          <div>Nter reserves the right to amend this Fair Usage Policy at any time. Changes will be effective upon
            posting the revised version on our platform or as otherwise notified.
          </div>
        </section>
        <section>
          <div class="heading">Contact Us</div>
          <div>For any questions or concerns regarding this policy, please contact us at <a href="mailto:helpdesk.nter.io">helpdesk.nter.io</a></div>
        </section>


      </div>
    </app-policy-article>
  `
})
export class FairUsagePolicyArticleComponent {

}
